import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronDown, BarChart3, TrendingUp, FileText, Video, ClipboardList, Phone, Mic, Package, Presentation, MessageCircle, Brain, Zap, Calendar } from 'lucide-react';

import LogoMarquee from '../components/home/LogoMarquee';
import ProcessSteps from '../components/home/ProcessSteps';
import StatsGrid from '../components/home/StatsGrid';
import WhyChannel from '../components/home/WhyChannel';
import CompareTable from '../components/home/CompareTable';
import FounderStory from '../components/home/FounderStory';
import FAQSection from '../components/home/FAQSection';
import BrandInquiryForm from '../components/home/BrandInquiryForm';
import StickyCTA from '../components/home/StickyCTA';
import LaunchCampaignChoiceModal from '../components/LaunchCampaignChoiceModal';

// Primary, hero-treatment offerings shown by default.
const HERO_OFFERINGS = [
  {
    icon: TrendingUp,
    label: 'Paid Endorsement Posts',
    desc: 'An executive or creator your buyers already follow writes a post vouching for your product — on LinkedIn, X, or in a top newsletter. In their voice, from their personal account. The fastest way to earn trust with pre-qualified buyers.',
    bullets: [
      "Written 100% in their voice — not a templated ad",
      'Published from their personal account, not your brand page',
      'You review and approve every post before it goes live',
      'Lives forever on their feed — earned reach for years',
    ],
  },
  {
    icon: Zap,
    label: 'Amplify + Paid Ads Management',
    desc: "We take your best endorsements and turn them into paid ads on the channels your buyers actually read. One team runs it all — you pre-pay the ad budget, we charge a $500 flat fee plus 15% of spend.",
    bullets: [
      'Ads on LinkedIn, X, Reddit, Meta, YouTube, and top newsletters',
      '$500 flat mgmt fee + 15% of ad spend — no hidden markups',
      'Amplify your Freddy exec content, or bring your own creative',
      'Weekly reports + unspent budget refunded at campaign close',
    ],
  },
];

// Secondary offerings revealed behind the "See all offering types" expander.
const OTHER_OFFERINGS = [
  { icon: Video,         label: 'Video Testimonials',    desc: '30–90 sec selfie videos — native-feel UGC ads + ABM outreach.' },
  { icon: ClipboardList, label: 'Surveys',               desc: 'Structured feedback from 10–50 senior buyers in your category.' },
  { icon: MessageCircle, label: 'Feedback Sessions',     desc: '30–60 min video calls with operators in your ICP.' },
  { icon: Phone,         label: 'Advisory Calls',        desc: '1-on-1 calls with executives who solved your problem.' },
  { icon: Mic,           label: 'Webinars & Podcasts',   desc: 'Executive-led live or recorded session you co-brand.' },
  { icon: Presentation,  label: 'Sales Pitch Meetings',  desc: 'Pay an executive to sit through your pitch — they set the rate.' },
  { icon: Calendar,      label: 'Event Promotion & Consulting', desc: 'End-to-end corporate event coordination — venue sourcing, executive speaker line-ups, on-site production, sponsor activations, and pre/post-event promotion across our executive network. From single dinners and roadshow stops to flagship annual summits. Book a strategy call for scoped pricing.', highlight: true },
  { icon: FileText,      label: 'Writing Services (Craft)', desc: 'Our writing team ghostwrites the copy that surrounds your endorsements — long-form blog articles, LinkedIn post cadences for execs, newsletter issues, sales one-pagers, whitepapers, case-study write-ups, and branded testimonials. Every piece runs through your brand voice guide, delivered ready to publish. From $500/piece or retainers from $2,000/mo.', highlight: true },
  { icon: Video,         label: 'Content Production & Shoots', desc: 'Freddy Media crew on-site — videographer, editor, producer. From $2,500/day.', highlight: true },
  { icon: Package,       label: 'Clipping & Editing Retainer',  desc: 'Monthly clip factory — 4 to 12+ short-form clips + long-form cuts, captions, brand-styled. From $1,500/mo.', highlight: true },
  { icon: Brain,         label: 'AI Training Q&A',       desc: "Train your company's Claude/OpenAI on real domain expertise — exec answers your questions, you download a fine-tuning dataset.", highlight: true },
];

// =============================================================
// Services section — 2 hero cards + click-to-expand for the rest
// =============================================================
function ServicesSection({ openLaunch }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="py-24 sm:py-32" data-testid="services-grid">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-14 sm:mb-20">
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-3">★ What we sell ★</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            How brands use us. <span className="text-[#FF4343]">Start simple</span>.
          </h2>
          <p className="text-sm sm:text-base text-[#9CA3AF] max-w-2xl mx-auto mt-4">
            Most brands start with one endorsement post from an executive their buyers trust —
            then we amplify the winners as paid ads. Testimonials, events, video, and 10+ other
            formats all run inside the same account, with one dedicated campaign manager.
          </p>
        </div>

        {/* Hero 2-card row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16">
          {HERO_OFFERINGS.map((o, i) => (
            <div key={i} data-testid={`hero-offering-${i}`}
              className="relative bg-gradient-to-br from-[#FF4343]/12 via-[#111827] to-[#111827] border border-[#FF4343]/50 p-7 sm:p-9 transition-all hover:-translate-y-1 hover:border-[#FF4343]">
              <span className="absolute top-3 right-3 text-[9px] font-mono uppercase tracking-wider text-[#FF4343] bg-[#090E33] border border-[#FF4343]/40 px-2 py-0.5">Flagship</span>
              <o.icon className="w-9 h-9 sm:w-10 sm:h-10 text-[#FF4343] mb-5" />
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-4 leading-tight">{o.label}</h3>
              <p className="text-xs sm:text-sm text-[#9CA3AF] leading-relaxed mb-5">{o.desc}</p>
              <ul className="space-y-2 mb-6">
                {o.bullets.map((b, j) => (
                  <li key={j} className="text-[11px] sm:text-xs text-[#9CA3AF] leading-relaxed flex items-start gap-2">
                    <span className="text-[#FF4343] mt-0.5 flex-shrink-0">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => openLaunch(`hero_offering_${i}`)}
                data-testid={`hero-offering-cta-${i}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase border-b-2 border-[#FF4343] text-[#FF4343] hover:text-white hover:border-white pb-0.5 transition-colors"
              >
                Book a kick-off call <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Expand row */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            data-testid="services-expander-toggle"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-mono uppercase tracking-wider text-[#9CA3AF] hover:text-white border border-white/15 hover:border-white/40 px-5 py-2.5 transition-all"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide other offerings' : 'See all offering types'}
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Expanded grid */}
        {expanded && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-10 animate-fade-in-up" data-testid="services-expanded-grid">
            {OTHER_OFFERINGS.map((o, i) => (
              <div
                key={i}
                data-testid={`offering-card-${i}`}
                className={`p-6 transition-all hover:-translate-y-0.5 ${o.highlight
                  ? 'bg-gradient-to-br from-[#FF4343]/10 to-[#111827] border border-[#FF4343]/40 hover:border-[#FF4343]'
                  : 'bg-[#111827] border border-white/10 hover:border-[#FF4343]/40'}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <o.icon className="w-7 h-7 text-[#FF4343]" />
                  {o.highlight && (
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[#FF4343] bg-[#FF4343]/15 px-1.5 py-0.5">NEW</span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-white mb-1.5">{o.label}</h3>
                <p className="text-xs sm:text-sm text-[#9CA3AF] leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchSource, setLaunchSource] = useState('home_hero');
  const openLaunch = (source) => { setLaunchSource(source); setLaunchOpen(true); };

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="home-page">
      <LaunchCampaignChoiceModal open={launchOpen} onClose={() => setLaunchOpen(false)} source={launchSource} />

      {/* ============ HERO ============ */}
      <section className="relative pt-16 pb-20 sm:pt-24 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#090E33] via-[#111827] to-[#090E33]" />
        {/* Decorative stars */}
        <div aria-hidden className="absolute top-20 left-10 text-[#FF4343]/20 text-2xl select-none rotate-12">★</div>
        <div aria-hidden className="absolute top-40 right-12 text-[#FF4343]/20 text-3xl select-none -rotate-12">★</div>
        <div aria-hidden className="absolute bottom-32 left-1/4 text-[#FF4343]/15 text-xl select-none rotate-45">★</div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <img
            src="/freddy-script-only.png"
            alt="Freddy Media"
            className="h-12 sm:h-16 mx-auto mb-8 animate-fade-in-up"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-4 animate-fade-in-up" data-testid="hero-tagline">
            ★ Endorsement Posts · Testimonials · Events ★
          </p>
          <h1
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter mb-6 animate-fade-in-up max-w-5xl mx-auto leading-[1.05]"
            data-testid="hero-heading"
            aria-label="Your next customers trust real executives, not ads"
          >
            Your next customers trust{' '}
            <span className="inline-block bg-[#FF4343] text-[#090E33] px-3 py-0.5 rounded-sm rotate-[-1.5deg] mx-1">
              real executives
            </span>{' '}
            &mdash; not ads.
          </h1>
          <p className="text-base sm:text-lg text-[#9CA3AF] max-w-2xl mx-auto mb-10 animate-fade-in-up stagger-1 leading-relaxed">
            We get trusted executives and creators <span className="text-white">posting about your brand</span>,
            <span className="text-white"> giving testimonials</span>, and
            <span className="text-white"> showing up at your events</span> — in front of the exact buyers you want.
            You approve everything. We run everything.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-in-up stagger-2">
            <button
              type="button"
              onClick={() => openLaunch('home_hero')}
              data-testid="hero-book-walkthrough-btn"
              className="rounded-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] hover:border-[#E03030] px-8 py-3.5 text-sm font-bold transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              Book a 20-min Walkthrough <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => document.getElementById('get-proposal')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-get-proposal-btn"
              className="rounded-full border-2 border-white/25 bg-transparent text-white hover:bg-white/5 hover:border-white px-8 py-3.5 text-sm font-bold transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              Get a Proposal in 24h
            </button>
          </div>
          <p className="text-[10px] text-[#6B7280] mt-5 animate-fade-in-up stagger-2">
            Fully managed by the Freddy Leads team. You approve every post before it goes live. Pay only on approval.
          </p>
        </div>
      </section>

      {/* ============ LOGO MARQUEE ============ */}
      <LogoMarquee />

      {/* ============ SERVICES GRID — 2 hero cards + expandable for the rest ============ */}
      <ServicesSection openLaunch={openLaunch} />

      {/* ============ PROCESS STEPS ============ */}
      <ProcessSteps />

      {/* ============ STATS ============ */}
      <StatsGrid />

      {/* ============ WHY CHANNEL (4-quadrant) ============ */}
      <WhyChannel />

      {/* ============ COMPARE TABLE ============ */}
      <CompareTable />

      {/* ============ FOUNDER STORY ============ */}
      <FounderStory />

      {/* ============ FAQ ============ */}
      <FAQSection />

      {/* ============ BRAND INQUIRY FORM ============ */}
      <BrandInquiryForm openLaunch={openLaunch} />

      {/* ============ STICKY CTA BAR ============ */}
      <StickyCTA openLaunch={openLaunch} />

      {/* ============ FINAL CTA BAND ============ */}
      <section className="py-20 sm:py-28 border-y border-white/5 bg-[#0B1138]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-3">★ Ready to get matched? ★</p>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-8">
            Pick a side. <span className="text-[#FF4343]">Get going.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              type="button"
              onClick={() => openLaunch('home_final_cta')}
              data-testid="cta-brand-final"
              className="rounded-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] hover:border-[#E03030] px-8 sm:px-10 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-all"
            >
              I&#39;m a Brand → <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/login?tab=register"
              data-testid="cta-exec-final"
              className="rounded-full border-2 border-white/25 bg-transparent text-white hover:bg-white/5 hover:border-white px-8 sm:px-10 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-all"
            >
              I&#39;m an Executive
            </Link>
          </div>
        </div>
      </section>

      {/* ============ POWERED BY FREDDY MEDIA (preserved) ============ */}
      <section className="py-8 sm:py-14 bg-white" data-testid="powered-by-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <img
                src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/lnrtssp0_freddy-logo-script.png"
                alt="Freddy Media"
                className="h-10 sm:h-14"
              />
              <div className="h-10 w-px bg-[#090E33]/10 hidden sm:block" />
              <div>
                <p className="text-[#090E33] text-sm font-semibold tracking-wide">Powered by Freddy Media</p>
                <p className="text-[#090E33]/50 text-xs mt-0.5">The Executive Endorsement Company</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/0razqlkv_freddy-logo-seal-symbol.png"
                alt="Freddy Media Seal"
                className="h-12 w-12 opacity-80"
              />
              <Link to="/for-brands" data-testid="powered-by-cta"
                className="border-2 border-[#090E33] bg-transparent text-[#090E33] hover:bg-[#090E33] hover:text-white px-6 py-2.5 text-sm font-semibold transition-all duration-300">
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER (preserved) ============ */}
      <footer className="py-10 sm:py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2.5">
                <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-6 w-6" />
                <span className="font-display text-lg font-bold">
                  <span className="text-[#FF4343]">Freddy</span>Leads
                </span>
                <span className="text-[#6B7280] text-xs ml-1">by Freddy Media</span>
              </div>
              <div className="flex gap-6">
                <Link to="/for-brands" className="text-sm text-[#6B7280] hover:text-white transition-colors">How It Works</Link>
                <Link to="/portal" className="text-sm text-[#6B7280] hover:text-white transition-colors">Brand Sign-in</Link>
                <Link to="/login" className="text-sm text-[#6B7280] hover:text-white transition-colors">Executive Sign-in</Link>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 border-t border-white/5">
              <p className="text-xs text-[#6B7280]">&copy; 2026 Freddy Media LLC. All rights reserved.</p>
              <div className="flex gap-4">
                <Link to="/terms" className="text-xs text-[#6B7280] hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link>
                <Link to="/privacy" className="text-xs text-[#6B7280] hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
                <a href="mailto:jon@freddymedia.com" className="text-xs text-[#6B7280] hover:text-white transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
